import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  authAPI, broadcastAuth, onAuthBroadcast, scheduleRefresh, setSessionEndedHandler, setTenantToken, stopRefreshTimer,
  watchSignedInMarker,
} from '../api/client';

const AuthContext = createContext(null);

// Identity comes from HttpOnly cookies shared by every *.ROOT_DOMAIN origin; nothing is kept in localStorage.
export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // Why there's no session: the person logged out (here or in another tab), or it ran out / never existed. Pages send
  // a logged-out person to a plain login page, and the others back to where they were after signing in.
  const [signedOut, setSignedOut] = useState(false);

  const ended = useCallback((byUser = false) => {
    stopRefreshTimer();
    setTenantToken(null);
    setSignedOut(byUser);
    setSession(null);
  }, []);

  const begin = useCallback((data) => {
    setSignedOut(false);
    setSession(data);
    scheduleRefresh(data?.access_expires_in);
    return data;
  }, []);

  const refresh = useCallback(async () => {
    try {
      // /me retries through /refresh automatically if the access token has expired.
      const res = await authAPI.me();
      return begin(res.data);
    } catch {
      ended();
      return null;
    }
  }, [begin, ended]);

  useEffect(() => {
    setSessionEndedHandler(() => ended());  // the session ran out
    refresh().finally(() => setLoading(false));
    // Keep every open tab in step: one tab logging out or refreshing updates the rest.
    const stopBroadcast = onAuthBroadcast((msg) => {
      if (msg?.type === 'logout') ended(true);
      if (msg?.type === 'refreshed' && msg.session) setSession(msg.session);
      if (msg?.type === 'login') refresh();
    });
    const stopWatching = watchSignedInMarker((now) => {
      if (!now) ended(true);  // logged out in a tab on another subdomain
      else refresh();         // a (possibly different) login happened elsewhere
    });
    return () => { stopBroadcast(); stopWatching(); };
  }, [ended, refresh]);

  /** Signs in, or returns {two_factor_required, challenge_id, email_hint} when an emailed code is needed next. */
  const login = async (email, password, rememberMe) => {
    const res = await authAPI.login(email, password, rememberMe);
    if (res.data.two_factor_required) return res.data;
    broadcastAuth({ type: 'login' });
    return begin(res.data);
  };

  const verifyLoginCode = async (challengeId, code) => {
    const res = await authAPI.verifyLoginCode(challengeId, code);
    broadcastAuth({ type: 'login' });
    return begin(res.data);
  };

  const register = async (data) => (await authAPI.register(data)).data;

  const logout = async ({ everywhere = false } = {}) => {
    try {
      await (everywhere ? authAPI.logoutAll() : authAPI.logout());
    } finally {
      broadcastAuth({ type: 'logout' });
      ended(true);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        signedOut,
        user: session?.user || null,
        memberships: session?.memberships || [],
        // Each account creates at most one organization (the server enforces this too).
        canCreateOrg: !!session && !(session.memberships || []).some((m) => m.role === 'owner'),
        loading,
        login,
        verifyLoginCode,
        register,
        logout,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
